import type { LngLat } from '@okmaps/shared';

/**
 * Parse a coordinate a user typed.
 *
 * Accepts decimal degrees and degrees/minutes/seconds in the forms people
 * actually paste - from a GPS unit, a phone, a spreadsheet, or a colleague's
 * email. Latitude first is assumed, because that is the convention in every
 * one of those sources, and a plain "-97.5, 35.5" is silently reordered when
 * that is the only reading that lands in Oklahoma.
 *
 * The full eight-coordinate-system entry the existing viewer offers - State
 * Plane, UTM 13/14/15, Albers, NAD27 - is deliberately not here. NAD27 in
 * particular needs datum transformation grids to be correct rather than
 * approximately right, and shipping a plausible-looking wrong answer to a
 * surveyor is worse than not offering it.
 */
export function parseCoordinates(input: string): LngLat | null {
  const text = input.trim();
  if (!text) return null;

  const dms = parseDms(text);
  if (dms) return inRange(dms) ? dms : null;

  const decimal = parseDecimal(text);
  if (decimal) return inRange(decimal) ? decimal : null;

  return null;
}

function parseDecimal(text: string): LngLat | null {
  const parts = text.split(/[,\s]+/).filter(Boolean);
  if (parts.length !== 2) return null;

  const a = Number(parts[0]);
  const b = Number(parts[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

  // Latitude first is the convention. Fall back to the other order only when
  // it is the reading that makes sense for Oklahoma.
  const latFirst = { lat: a, lng: b };
  if (inRange(latFirst)) return latFirst;

  const lngFirst = { lat: b, lng: a };
  if (inRange(lngFirst)) return lngFirst;

  return latFirst;
}

const DMS = /(\d+(?:\.\d+)?)\s*[°d:\s]\s*(\d+(?:\.\d+)?)\s*['m:\s]\s*(\d+(?:\.\d+)?)?\s*["s]?\s*([NSEW])?/gi;

function parseDms(text: string): LngLat | null {
  const matches = [...text.matchAll(DMS)];
  if (matches.length !== 2) return null;

  const values = matches.map((m) => {
    const deg = Number(m[1]);
    const min = Number(m[2] ?? 0);
    const sec = Number(m[3] ?? 0);
    const hemisphere = (m[4] ?? '').toUpperCase();
    const magnitude = deg + min / 60 + sec / 3600;
    const negative = hemisphere === 'S' || hemisphere === 'W' || /^-/.test(m[0].trim());
    return { value: negative ? -magnitude : magnitude, hemisphere };
  });

  const [first, second] = values as [(typeof values)[0], (typeof values)[0]];

  // Use the hemisphere letters when present; otherwise assume latitude first.
  if (first.hemisphere === 'E' || first.hemisphere === 'W') {
    return { lng: first.value, lat: second.value };
  }
  return { lat: first.value, lng: second.value };
}

/** Oklahoma, with a small margin so a point just over the line still resolves. */
function inRange(p: LngLat): boolean {
  return p.lat >= 33.0 && p.lat <= 37.6 && p.lng >= -103.6 && p.lng <= -93.9;
}
